import React, { useMemo, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import { Button } from 'design-system';
import Spacer from 'design-system/Spacer';
import Links from 'utils/links';
import useColors from 'hooks/useColors';
import usePasscodeModal from 'hooks/usePasscodeModal';
import { formatAmount, formatPlural } from 'utils/formatting';
import FXFeeRow from './FXFeeRow';
import RowAmount from './RowAmount';
import TapableText from './TapableText';
import { useStockMeta } from '../hooks';
import AboutInfoRow from './AboutInfoRow';
import { formatNumberOfShares } from '../utils';
import { useCurrentPrice } from '../hooks/selectors';
import { orderConfirmViewStrings } from './OrderConfirmView';
import { getLimitPriceAndTotalCostForLimitBuy } from '../utils/format';
import { OrderType } from '../types';
const LimitBuyOrderConfirmView = ({ assetId, symbol, shares, execute, fxConversion, }) => {
    const colors = useColors();
    const [getPin] = usePasscodeModal();
    const stockCache = useStockMeta(assetId);
    const currentPrice = useCurrentPrice(assetId);
    const amountObj = useMemo(() => getLimitPriceAndTotalCostForLimitBuy(shares, currentPrice, fxConversion), [currentPrice, fxConversion, shares]);
    const handleConfirm = useCallback(async (forcePinAuthentication) => {
        const biometryDescription = `You are adding Limit order of ${formatAmount(amountObj.totalAmount.value, amountObj.totalAmount.currency, 2, false)} for $${symbol}`;
        try {
            const pin = await getPin({
                title: 'Enter Your Pin Code',
                biometryDescription,
                forcePinAuthentication: typeof forcePinAuthentication === 'boolean' ? forcePinAuthentication : false,
            });
            try {
                await execute({ pin, symbol, amount: amountObj.limitPrice, shares, orderType: OrderType.LIMIT });
            }
            catch (e) {
                // Other errors are already handled in execute
                if ('message' in e && e.message === 'Refresh token expired') {
                    handleConfirm(true);
                }
            }
        }
        catch {
            // use cancelled
        }
    }, [amountObj.limitPrice, amountObj.totalAmount.currency, amountObj.totalAmount.value, execute, getPin, shares, symbol]);
    const handleLearnMore = useCallback(() => {
        Links.openKnownLink('buyInShares');
    }, []);
    return (<View>
      <Spacer h={46}/>
      <View style={styles.row}>
        <Text Text-20 flex>
          Buy ${symbol}
        </Text>
        <Logo R44 uri={stockCache?.iconUrl}/>
      </View>
      <Spacer h={8}/>
      <AboutInfoRow title="Order type" info={`Limit buy`} noPaddingHorizontal/>
      <AboutInfoRow noPaddingHorizontal info={amountObj.limitPriceFormatted} title={`Limit price`}/>
      <AboutInfoRow title="No. of shares" info={formatNumberOfShares(shares, false, false, null)} noPaddingHorizontal/>
      <FXFeeRow fxConversion={fxConversion}/>
      <RowAmount est bold showCents noBottomLine noPaddingHorizontal amount={amountObj.totalAmount} title={orderConfirmViewStrings.totalCost}/>
      <Spacer h={16}/>
      <Text TextThin-14 Secondary centered>
        You are placing a good for day limit order to buy {shares} {formatPlural('share', 'shares', shares)} of $
        {symbol}. Your pending order, if executed, will execute at a limit price of {amountObj.limitPriceFormatted} per
        share or better. The limit price is set at 5% above the last traded price.{' '}
        <Text Primary TextThin-14 onPress={handleLearnMore}>
          Learn more
        </Text>
      </Text>
      <Spacer h={16}/>
      <Spacer h={16}/>
      <TapableText pdf Gray centered TextThin-12 linkColor={colors.text.brand} links={orderConfirmViewStrings.links} text={orderConfirmViewStrings.conditions}/>
      <Spacer h={32}/>
      <Button title="Confirm" brand onPress={handleConfirm}/>
    </View>);
};
export default LimitBuyOrderConfirmView;
const styles = StyleSheet.create({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
});
